// built-from: /engagement-context-panel v1 impl:engagement-context-panel role:implementation
// useEngagementContext - the API boundary for the context a consultant adds to
// an engagement or to one activity (feature 092-engagement-context-items).
//
// The panel reaches the API ONLY through this hook (constitution III: never
// `fetch` inside a component). Every call resolves a typed result and never
// throws: a refusal carries the server's own message so the panel can show the
// consultant exactly what to do next, and a transport failure degrades to a
// plain message rather than a crash. Nothing here decides what to render.
//
// 093: the browser session now carries the name the consultant signed in with,
// so `addedBy` is sent from there. A caller may still name someone explicitly,
// and with nobody signed in the field is left off entirely.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getUsername } from '../utils/authSession';

export type ContextItemScope = 'engagement' | { activityId: string };

export interface ContextItemFile {
  name: string;
  mime: string;
  size: number;
  path: string;
}

export interface ContextItem {
  id: string;
  title: string;
  scope: ContextItemScope;
  kind: 'text' | 'files';
  files: ContextItemFile[];
  companionPath: string;
  pagePath: string;
  addedBy: string;
  addedAt: string;
  summary: string;
}

export interface ContextItemInput {
  title: string;
  text?: string;
  files?: File[];
  addedBy?: string;
}

export type ContextListResult =
  | { ok: true; items: ContextItem[] }
  | { ok: false; message: string };

export type ContextAddResult =
  | { ok: true; item: ContextItem }
  | { ok: false; message: string };

export type ContextRemoveResult =
  | { ok: true; id: string }
  | { ok: false; message: string };

export type ContextStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseEngagementContextReturn {
  items: ContextItem[];
  status: ContextStatus;
  error: string | null;
  refresh: () => Promise<ContextListResult>;
  addItem: (input: ContextItemInput) => Promise<ContextAddResult>;
  removeItem: (id: string) => Promise<ContextRemoveResult>;
}

export const CONTEXT_LOAD_FAILED = 'Could not load the context for this screen. Try again.';
export const CONTEXT_ADD_FAILED = 'Could not add this context. Your details are kept here. Try again.';
export const CONTEXT_REMOVE_FAILED = 'Could not remove this context. It is still here. Try again.';

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readFiles(value: unknown): ContextItemFile[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    const file = candidate as { name?: unknown; mime?: unknown; size?: unknown; path?: unknown };
    if (typeof file?.name !== 'string' || !file.name) return [];
    return [{
      name: file.name,
      mime: readString(file.mime),
      size: typeof file.size === 'number' ? file.size : 0,
      path: readString(file.path),
    }];
  });
}

function readScope(value: unknown): ContextItemScope {
  const activityId = (value as { activityId?: unknown } | null)?.activityId;
  if (typeof activityId === 'string' && activityId) return { activityId };
  return 'engagement';
}

export function readContextItem(value: unknown): ContextItem | null {
  const record = value as Record<string, unknown> | null;
  if (!record || typeof record.id !== 'string' || !record.id) return null;
  if (typeof record.title !== 'string' || !record.title) return null;
  return {
    id: record.id,
    title: record.title,
    scope: readScope(record.scope),
    kind: record.kind === 'files' ? 'files' : 'text',
    files: readFiles(record.files),
    companionPath: readString(record.companionPath),
    pagePath: readString(record.pagePath),
    addedBy: readString(record.addedBy),
    addedAt: readString(record.addedAt),
    summary: readString(record.summary),
  };
}

function readItems(body: unknown): ContextItem[] {
  const items = (body as { items?: unknown } | null)?.items;
  if (!Array.isArray(items)) return [];
  return items.flatMap((candidate) => {
    const item = readContextItem(candidate);
    return item ? [item] : [];
  });
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function messageFrom(body: unknown, fallback: string): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

export function useEngagementContext(
  engagementId: string | null,
  activityId?: string | null,
): UseEngagementContextReturn {
  const [items, setItems] = useState<ContextItem[]>([]);
  const [status, setStatus] = useState<ContextStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const listUrl = engagementId
    ? `/api/engagements/${encodeURIComponent(engagementId)}/context${
      activityId ? `?activityId=${encodeURIComponent(activityId)}` : ''}`
    : null;
  const collectionUrl = engagementId
    ? `/api/engagements/${encodeURIComponent(engagementId)}/context`
    : null;

  const refresh = useCallback(async (): Promise<ContextListResult> => {
    if (!listUrl) {
      if (liveRef.current) {
        setItems([]);
        setStatus('idle');
        setError(null);
      }
      return { ok: false, message: CONTEXT_LOAD_FAILED };
    }
    if (liveRef.current) {
      setStatus('loading');
      setError(null);
    }
    try {
      const response = await fetch(listUrl);
      if (!response.ok) {
        if (liveRef.current) {
          setStatus('error');
          setError(CONTEXT_LOAD_FAILED);
        }
        return { ok: false, message: CONTEXT_LOAD_FAILED };
      }
      const next = readItems(await readJson(response));
      if (liveRef.current) {
        setItems(next);
        setStatus('ready');
      }
      return { ok: true, items: next };
    } catch {
      if (liveRef.current) {
        setStatus('error');
        setError(CONTEXT_LOAD_FAILED);
      }
      return { ok: false, message: CONTEXT_LOAD_FAILED };
    }
  }, [listUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addItem = useCallback(async (input: ContextItemInput): Promise<ContextAddResult> => {
    if (!collectionUrl) return { ok: false, message: CONTEXT_ADD_FAILED };
    const form = new FormData();
    form.append('title', input.title);
    if (input.text) form.append('text', input.text);
    if (activityId) form.append('activityId', activityId);
    const addedBy = (input.addedBy || getUsername()).trim();
    if (addedBy) form.append('addedBy', addedBy);
    (input.files || []).forEach((file) => form.append('files', file, file.name));
    try {
      const response = await fetch(collectionUrl, { method: 'POST', body: form });
      const body = await readJson(response);
      if (!response.ok) return { ok: false, message: messageFrom(body, CONTEXT_ADD_FAILED) };
      const item = readContextItem((body as { item?: unknown } | null)?.item);
      if (!item) return { ok: false, message: CONTEXT_ADD_FAILED };
      if (liveRef.current) setItems((current) => [item, ...current]);
      return { ok: true, item };
    } catch {
      return { ok: false, message: CONTEXT_ADD_FAILED };
    }
  }, [activityId, collectionUrl]);

  const removeItem = useCallback(async (id: string): Promise<ContextRemoveResult> => {
    if (!collectionUrl || !id) return { ok: false, message: CONTEXT_REMOVE_FAILED };
    try {
      const response = await fetch(`${collectionUrl}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!response.ok) {
        const body = await readJson(response);
        return { ok: false, message: messageFrom(body, CONTEXT_REMOVE_FAILED) };
      }
      if (liveRef.current) setItems((current) => current.filter((item) => item.id !== id));
      return { ok: true, id };
    } catch {
      return { ok: false, message: CONTEXT_REMOVE_FAILED };
    }
  }, [collectionUrl]);

  return { items, status, error, refresh, addItem, removeItem };
}

export default useEngagementContext;
